import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronRight,
  Clock,
  Users,
  Star,
  Compass,
  BookOpen,
  FileText,
  Eye,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  getFeaturedCourses,
  getFeaturedDiagrams,
} from "@/services/contentService";
import { Badge } from "@/components/ui/badge";

interface FeaturedItemsProps {
  type: "courses" | "diagrams";
  maxItems?: number;
}

// Define interfaces for Course and Diagram
interface BaseFeaturedItem {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  image?: string;
  created_at?: string;
  status?: string;
  featured?: boolean;
}

interface FeaturedCourse extends BaseFeaturedItem {
  duration?: string;
  studentCount?: number;
  rating?: number;
  category_id?: string;
  category_name?: string;
}

interface FeaturedDiagram extends BaseFeaturedItem {
  category?: string;
  category_id?: string;
  category_name?: string;
  viewCount?: number;
  type?: string; // Citta, Cetasika, Rupa, etc.
}

type FeaturedItem = FeaturedCourse | FeaturedDiagram;

export default function FeaturedItems({
  type,
  maxItems = 4,
}: FeaturedItemsProps) {
  const { t } = useTranslation("common");
  const [items, setItems] = useState<FeaturedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cải thiện: Sử dụng useMemo để tránh tạo hàm mới mỗi lần render
  const fetchItemsFn = useMemo(
    () => async () => {
      try {
        setIsLoading(true);
        setError(null);

        let fetchedItems: FeaturedItem[] = [];
        if (type === "courses") {
          // Add type assertion to fix TypeScript error
          fetchedItems = (await getFeaturedCourses()) as FeaturedCourse[];
        } else if (type === "diagrams") {
          // Add type assertion to fix TypeScript error
          fetchedItems = (await getFeaturedDiagrams()) as FeaturedDiagram[];
        }

        setItems(fetchedItems.slice(0, maxItems));
      } catch (err) {
        console.error(`Error fetching featured ${type}:`, err);
        setError(t("errors.somethingWentWrong"));
      } finally {
        setIsLoading(false);
      }
    },
    [type, maxItems, t],
  );

  // Cải thiện: Sử dụng dependency array để tránh fetch nhiều lần
  useEffect(() => {
    fetchItemsFn();

    // Cải thiện: Chỉ fetch lại sau mỗi 5 phút
    const intervalId = setInterval(
      () => {
        fetchItemsFn();
      },
      5 * 60 * 1000,
    );

    return () => clearInterval(intervalId);
  }, [fetchItemsFn]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
        {[...Array(maxItems)].map((_, index) => (
          <Card key={index} className="overflow-hidden animate-pulse">
            <div className="h-36 bg-muted"></div>
            <CardContent className="p-3 md:p-4">
              <div className="h-6 bg-muted rounded mb-2"></div>
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || items.length === 0) {
    return null;
  }

  // Sử dụng key translation cụ thể thay vì dynamic key
  const titleTranslationKey = type === "courses" ? "featuredCourses" : "featuredDiagrams";

  return (
    <section
      className={`py-6 ${type === "diagrams" ? "bg-muted/30" : "bg-background"}`}
    >
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">
              {t(titleTranslationKey)}
            </h2>
            <Link
              to={`/${type}`}
              className="flex items-center text-sm text-primary hover:underline"
            >
              {t("featuredViewAll")}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
            {items.map((item) => (
              <FeaturedItemCard key={item.id} item={item} type={type} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

interface FeaturedItemCardProps {
  item: FeaturedItem;
  type: "courses" | "diagrams";
}

const FeaturedItemCard: React.FC<FeaturedItemCardProps> = ({ item, type }) => {
  const { t } = useTranslation("common");
  // Use the appropriate title field based on the item type
  const title = item.title || item.name || "";
  const description = item.description || "";
  const linkUrl = `/${type === "courses" ? "courses" : "diagrams"}/${item.id}`;

  return (
    <Link to={linkUrl} className="block h-full">
      <Card className="h-full flex flex-col overflow-hidden border border-border/40 hover:border-primary/40 transition-all">
        <div className="relative h-36 overflow-hidden">
          {item.image ? (
            <img
              src={item.image}
              alt={title}
              className="w-full h-full object-cover transition-transform hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              {type === "courses" ? (
                <BookOpen className="h-12 w-12 text-muted-foreground/40" />
              ) : (
                <FileText className="h-12 w-12 text-muted-foreground/40" />
              )}
            </div>
          )}
        </div>
        <CardContent className="flex-grow flex flex-col p-3 md:p-4">
          {type === "courses" && (item as FeaturedCourse).category_name && (
            <Badge variant="outline" className="mb-2 w-fit">
              {(item as FeaturedCourse).category_name}
            </Badge>
          )}
          {type === "diagrams" && (item as FeaturedDiagram).type && (
            <Badge variant="outline" className="mb-2 w-fit">
              {(item as FeaturedDiagram).type}
            </Badge>
          )}
          <CardTitle className="text-lg mb-2 line-clamp-2">{title}</CardTitle>
          <CardDescription className="line-clamp-2 mb-3">
            {description && description.length > 150
              ? description.substring(0, 150) + "..."
              : description}
          </CardDescription>
          <div className="mt-auto flex items-center justify-between">
            {type === "courses" && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Clock className="mr-1 h-4 w-4" />
                <span>{(item as FeaturedCourse).duration || t("notFound")}</span>
              </div>
            )}
            <Button variant="ghost" size="sm" className="ml-auto">
              {t("viewDetails")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
